import React, { useState } from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import * as Types from 'common/utils/Types'
import { Navigate } from 'react-router-dom'
import TagSelect from 'common/components/TagSelect'
import { Tag } from 'types'

const LastCurrency = "expected-movements-last-currency"

interface Props {
    onSuccess: string | ((result: unknown) => void)
    static?: boolean
    entryId?: number
    currentPlanId?: number
}

function EditExpectedMovementWorkflow(props: Props) {

    const [redirect, setRedirect] = useState<string | null>(null)

    if (redirect) {
        return <Navigate to={redirect} replace />
    }

    const loadingStep = (_parameters: unknown) => {
        const dataToFetch: Record<string, any> = {
            "currencies": API.get('/currencies'),
            "plans": API.get('/budget-plan'),
            "tags": API.get('/tags'),
            "lastCurrency": API.getVariable(LastCurrency, "int")
        }
        if (props.entryId !== undefined) {
            dataToFetch.entry = API.get(`/expected-movements/${props.entryId}`)
        }
        return API.multiple(dataToFetch)
            .then((r: any) => ({
                currencies: r.currencies.results,
                entry: r["entry"],
                plans: r.plans.results,
                tags: r.tags.results,
                lastCurrency: r.lastCurrency.value
            }))
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, _values: unknown, _errors: unknown, completion: (input: unknown) => void) => {
        const formEl = form(loading_result as any, completion)
        return { component: formEl, title: props.entryId ? "Edit expected movement" : "New expected movement" }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        const requests: Record<string, any> = {
            "var": API.setVariable(LastCurrency, (user_input as any).currencyId, "int")
        }
        if (props.entryId) {
            requests["insert"] = API.put(`/expected-movements/${props.entryId}`, user_input)
        } else {
            requests["insert"] = API.post("/expected-movements", user_input)
        }
        return API.multiple(requests)
    }

    const completionCallback = (execution_result: unknown) => {
        if (Types.isString(props.onSuccess)) {
            setRedirect(props.onSuccess as string)
        } else {
            (props.onSuccess as (result: unknown) => void)(execution_result)
        }
    }

    function form(loading_result: any, completion: (input: unknown) => void) {
        return <FormInner loading_result={loading_result} completion={completion} currentPlanId={props.currentPlanId} />
    }

    return <OperationWorkflowModal
        static={props.static}
        loadingStep={loadingStep}
        userInputStep={userInputStep}
        executionStep={executionStep}
        completionCallback={completionCallback}
    />
}

interface FormInnerProps {
    loading_result: any
    completion: (input: unknown) => void
    currentPlanId?: number
}

function FormInner({ loading_result, completion, currentPlanId }: FormInnerProps) {
    const previousValues = loading_result.entry
    const [availableTags, setAvailableTags] = useState<Tag[]>(loading_result.tags)
    const [tagIds, setTagIds] = useState<number[]>(
        previousValues ? (previousValues.tags ?? []).map((t: Tag) => t.id) : []
    )

    const parseForm = (values: any) => {
        completion({
            ...values,
            amount: values.amount.trim(),
            tagIds
        })
    }

    const schema = yup.object().shape({
        summary: yup.string().required().trim(),
        amount: yup.number().required(),
        repeatsAfterDays: yup.number().required(),
        currencyId: yup.number().required(),
        granted: yup.boolean()
    })

    const currencies = loading_result.currencies.map((item: any) => <option value={item.id} key={`cur${item.id}`}>{item.name}</option>)
    const repeatValues = [
        { value: 1, label: "Daily" },
        { value: 7, label: "Weekly" },
        { value: 14, label: "Every two weeks" },
        { value: 30, label: "Monthly" }
    ]
    const repeatOptions = repeatValues.map((item) => <option value={item.value} key={`rep${item.value}`}>{item.label}</option>)

    const initialValues = previousValues ? {
        summary: previousValues.summary,
        amount: previousValues.amount,
        repeatsAfterDays: previousValues.repeatsAfterDays,
        currencyId: previousValues.currency.id,
        granted: previousValues.granted,
        budgetPlanId: previousValues.budgetPlan.id
    } : {
        summary: "",
        amount: "",
        repeatsAfterDays: repeatValues[0].value,
        currencyId: loading_result.lastCurrency || loading_result.currencies[0].id,
        granted: false,
        budgetPlanId: currentPlanId
    }

    return <>
        <Formik
            validationSchema={schema}
            onSubmit={parseForm}
            initialValues={initialValues}
        >
            {({
                handleSubmit,
                handleChange,
                values,
                errors,
            }) => (
                <Form noValidate onSubmit={handleSubmit}>

                    <Form.Group controlId="formSummary">
                        <Form.Label>Summary</Form.Label>
                        <Form.Control
                            type='text'
                            name='summary'
                            placeholder="Summary"
                            value={values.summary}
                            onChange={handleChange}
                            isInvalid={!!errors.summary}
                        />
                        <Form.Control.Feedback type="invalid">{errors.summary}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Row>
                        <Form.Group as={Col} controlId="formAmount">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control
                                type='text'
                                name='amount'
                                placeholder="-10.00"
                                value={values.amount}
                                onChange={handleChange}
                                isInvalid={!!errors.amount}
                            />
                            <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} controlId="formCurrency">
                            <Form.Label>Currency</Form.Label>
                            <Form.Control
                                as='select'
                                name='currencyId'
                                value={values.currencyId}
                                onChange={handleChange}
                                isInvalid={!!(errors as any).currency}
                            >
                                {currencies}
                            </Form.Control>
                        </Form.Group>
                    </Form.Row>

                    <Form.Group controlId="formRepeat">
                        <Form.Label>Repeats every</Form.Label>
                        <Form.Control
                            as='select'
                            name='repeatsAfterDays'
                            value={values.repeatsAfterDays}
                            onChange={handleChange}
                            isInvalid={!!errors.repeatsAfterDays}
                        >
                            {repeatOptions}
                        </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="formTags">
                        <Form.Label>Tags</Form.Label>
                        <TagSelect
                            selectedTagIds={tagIds}
                            availableTags={availableTags}
                            onChange={(ids, updatedTags) => {
                                setTagIds(ids)
                                setAvailableTags(updatedTags)
                            }}
                        />
                    </Form.Group>

                    <Form.Group controlId="formGranted">
                        <Form.Check
                            type='checkbox'
                            label='Exact amount guaranteed for each month'
                            name='granted'
                            checked={values.granted}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Button variant="primary" type="submit">Save</Button>

                </Form>
            )}
        </Formik>
    </>
}

export default EditExpectedMovementWorkflow
